/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable theme-colors/no-literal-colors */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  css,
  Currency,
  ensureIsArray,
  getCurrencySymbol,
  styled,
  t,
  useTheme,
} from '@superset-ui/core';
import { CSSObject } from '@emotion/react';
import { Select } from 'src/components';
import { ViewState } from 'src/views/types';
import { SelectProps } from 'src/components/Select/types';
import ControlHeader from '../../ControlHeader';

export interface CurrencyControlProps {
  onChange: (currency: Partial<Currency>) => void;
  value?: Partial<Currency>;
  symbolSelectOverrideProps?: Partial<SelectProps>;
  currencySelectOverrideProps?: Partial<SelectProps>;
  symbolSelectAdditionalStyles?: CSSObject;
  currencySelectAdditionalStyles?: CSSObject;
}

const CurrencyControlContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;

    & > :first-child {
      margin-right: ${theme.gridUnit * 4}px;
      min-width: 0;
      flex: 1;
    }

    & > :nth-child(2) {
      min-width: 0;
      flex: 1;
    }

    .custom-symbol-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: ${theme.gridUnit * 2}px;
    }

    .custom-symbol-container input {
      margin-right: ${theme.gridUnit * 2}px;
    }
  `}
`;

export const CURRENCY_SYMBOL_POSITION_OPTIONS = [
  { value: 'prefix', label: t('前缀') },
  { value: 'suffix', label: t('后缀') },
];

export const CurrencyControl = ({
  onChange,
  value: currencyProp = { symbol: '', symbolPosition: 'prefix' }, // 确保默认值包含必要属性
  symbolSelectOverrideProps = {},
  currencySelectOverrideProps = {},
  symbolSelectAdditionalStyles,
  currencySelectAdditionalStyles,
  ...props
}: CurrencyControlProps) => {
  const theme = useTheme();
  const currencies = useSelector<ViewState, string[]>(
    state => state.common?.currencies || [],
  );

  const currenciesOptions = useMemo(
    () => [
      { value: '自定义', label: t('自定义') }, // 将自定义选项放到首位
      ...ensureIsArray(currencies).map(currencyCode => ({
        value: currencyCode,
        label: getCurrencySymbol({ symbol: currencyCode }),
        title: getCurrencySymbol({ symbol: currencyCode }),
      })),
    ],
    [currencies],
  );

  const currency = useMemo(() => ({ ...currencyProp }), [currencyProp]);

  const [customSymbol, setCustomSymbol] = useState<string>(
    currency?.symbol || '', // 使用可选链操作符
  );

  const handleCurrencyChange = (symbol: string) => {
    if (symbol === '自定义') {
      setCustomSymbol('');
    }
    onChange({ ...currency, symbol });
  };

  const handleCustomSymbolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSymbol = e.target.value;
    setCustomSymbol(newSymbol);
  };

  const handleCustomSymbolConfirm = () => {
    onChange({ ...currency, symbol: customSymbol });
  };

  return (
    <>
      <ControlHeader {...props} />
      <CurrencyControlContainer
        css={css`
          & > :first-of-type {
            ${symbolSelectAdditionalStyles};
          }
          & > :nth-of-type(2) {
            ${currencySelectAdditionalStyles};
          }
        `}
        className="currency-control-container"
      >
        <Select
          ariaLabel={t('自定义前缀或后缀')}
          options={CURRENCY_SYMBOL_POSITION_OPTIONS}
          placeholder={t('前缀或后缀')}
          onChange={(symbolPosition: string) => {
            onChange({ ...currency, symbolPosition });
          }}
          onClear={() => onChange({ ...currency, symbolPosition: undefined })}
          value={currency?.symbolPosition}
          allowClear
          {...symbolSelectOverrideProps}
        />
        <div>
          <Select
            ariaLabel={t('Currency symbol')}
            options={currenciesOptions}
            placeholder={t('自定义')}
            onChange={handleCurrencyChange}
            onClear={() => {
              onChange({ ...currency, symbol: undefined });
              setCustomSymbol('');
            }}
            value={currency?.symbol === '自定义' ? '自定义' : currency?.symbol}
            allowClear
            allowNewOptions={false} // 禁止在下拉菜单中添加新选项
            {...currencySelectOverrideProps}
          />
        </div>
      </CurrencyControlContainer>
      {currency?.symbol === '自定义' && (
        <div
          className="custom-symbol-container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <input
            className="ant-input"
            value={customSymbol}
            onChange={handleCustomSymbolChange}
            placeholder={t('请输入自定义前后缀...')}
            style={{
              marginTop: 8,
              borderRadius: 4,
              width: '74%',
              height: '30.67px',
            }}
          />
          <button
            style={{
              marginTop: 8,
              height: '30.67px',
              width: '20%',
              marginLeft: '6%',
              borderRadius: 4,
              border: '1px solid #d9d9d9',
              background: 'rgba(71, 85, 171, 0.11)',
              color: theme.colors.primary.base,
            }}
            onClick={handleCustomSymbolConfirm}
            type="button"
          >
            {t('确认')}
          </button>
        </div>
      )}
    </>
  );
};
